/*
  PLUSH + OAK — CART RATE
  --------------------------------------------------------------------------
  The cart and the drawer both carried "Spend $7,321.00 more and get free
  shipping!" — a deficit message against a threshold four web orders a year
  reached, on the last screen before checkout. It told the buyer they had
  failed, then sent them into checkout with no idea what delivery costs. Free
  shipping was switched off entirely on 2026-09-19; the threshold it measured
  against no longer exists.

  This states the real rate and the real total instead. The figure is never
  hardcoded: it comes from /cart/shipping_rates.json, which is Shopify pricing
  the actual cart through the same delivery profiles checkout will use. So the
  accessory profiles, the Alaska and Hawaii lanes and the Canadian rate are
  all right by construction rather than by us guessing.

  WHERE THE DESTINATION COMES FROM. It used to come from the product page —
  the shopper answered a postcode check and po-freight-note.js stored it. That
  check is gone, because Unlimited Delivery is now one flat rate across the
  whole contiguous US and one flat rate across every Canadian province, so
  there was nothing left to ask. With nothing stored, this falls back to a
  representative destination inside the shopper's own market and quotes that.
  For everyone except Alaska and Hawaii that IS their rate, and those two are
  named as an exception rather than quietly mispriced.

  A stored destination still wins when one exists, so a shopper who answered
  the check before it was removed keeps their exact figure, Alaska included.

  ⚠ That endpoint is not read-only. Querying it for an address with no
  available rate makes Shopify drop the unshippable items — the cart empties.
  The fallback destinations below are deliberately ordinary addresses in zones
  that always rate: since 2026-09-19 the Freight profile has a $1 floor and no
  ceiling, so a bed cart of any value quotes. Do not point this at a zone
  without rates, and do not remove a zone to mean "contact us".
*/
(function () {
  'use strict';

  var STORE_KEY = 'po-destination';

  var NO_DEST = 'Flat-rate delivery — one fee covers your whole order, however many pieces.';

  /* Ordinary, central addresses in each market. Nothing is shown to the
     shopper about them — they exist only to make Shopify price the cart. */
  var FALLBACK = {
    US: { country: 'United States', province: 'TX', zip: '75001' },
    CA: { country: 'Canada', province: 'ON', zip: 'M5V 2T6' }
  };

  /* ---- What the delivery row is called ------------------------------------
     Shopify names the rate after the delivery method, but only while every item
     in the cart comes from ONE delivery profile. A bed plus any accessory spans
     two: Shopify throws both names away, sums the rates and returns the generic
     "Shipping". So a bed on its own read "Unlimited Furniture Delivery (USA)"
     and the identical cart with a $449 pillow added read "Shipping" — at the
     same $379. Courtney, 2026-09-19: two labels only, chosen by what is in the
     cart, never by what Shopify happened to call the rate. Furniture present is
     Unlimited Furniture Delivery; swatches or accessories on their own are just
     Shipping, "nothing special".

     The type list is the one in theme.liquid that gates po-freight-note.js onto
     the product page. Keep the two identical — a type in one and not the other
     is a product whose PDP promises Unlimited Delivery and whose cart does not,
     or the reverse. */
  var FURNITURE_TYPES = ['Curved Salon Bed', 'Electric Salon Bed', 'Massage Table', 'Lash Bed'];

  function hasFurniture(cart) {
    if (!cart || !cart.items) return false;
    for (var i = 0; i < cart.items.length; i++) {
      if (FURNITURE_TYPES.indexOf(cart.items[i].product_type) !== -1) return true;
    }
    return false;
  }

  function rowLabel(furniture) {
    return furniture ? 'Unlimited Furniture Delivery' : 'Shipping';
  }

  var cache = {};

  function market() {
    var iso = '';
    try { iso = (window.Shopify && window.Shopify.country) || ''; } catch (e) {}
    if (!iso) {
      try {
        var cur = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
        iso = (cur === 'CAD') ? 'CA' : 'US';
      } catch (e) { iso = 'US'; }
    }
    return String(iso).toUpperCase();
  }

  /* An exact destination the shopper gave us, if there is one. */
  function storedDestination() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      return (d && d.country && d.province && d.zip) ? d : null;
    } catch (e) {
      return null;
    }
  }

  function destination() {
    var stored = storedDestination();
    if (stored) return stored;
    var fb = FALLBACK[market()];
    if (!fb) return null;
    /* Flagged so the copy never claims to know where they live. */
    return { country: fb.country, province: fb.province, zip: fb.zip, assumed: true };
  }

  function money(value) {
    var n = parseFloat(value);
    if (isNaN(n)) return null;
    /* Whole dollars read as a price; cents only appear when they are real. */
    var body = (n % 1 === 0) ? String(n) : n.toFixed(2);
    return '$' + body.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Shopify returns every eligible service. Where several apply the cheapest is
     quoted, and the copy says so rather than implying it is the only price. */
  function cheapest(rates) {
    var best = null;
    for (var i = 0; i < rates.length; i++) {
      var price = parseFloat(rates[i].price);
      if (isNaN(price)) continue;
      if (!best || price < best.price) best = { price: price, name: rates[i].name };
    }
    return best;
  }

  /* How many DIFFERENT prices the shopper is choosing between. The accessory
     profile returns Commercial and Residential as separate services at the
     same $28, and counting services made the cart say "From $477" — which
     tells a shopper the price might go up when it cannot. Only a real spread
     earns the hedge. */
  function priceChoices(rates) {
    var seen = {};
    var n = 0;
    for (var i = 0; i < rates.length; i++) {
      var price = parseFloat(rates[i].price);
      if (isNaN(price)) continue;
      if (!seen[price]) { seen[price] = true; n++; }
    }
    return n;
  }

  function sentence(best, count, dest, furniture) {
    /* No rate at all is a real answer, not a missing one — some lanes are
       quoted by hand. Saying "flat-rate delivery" here would promise a price
       that does not exist for them. */
    if (!best) {
      if (dest.assumed) return NO_DEST;
      return 'Delivery to ' + dest.province + ' is quoted individually — ' +
             'contact us and we will price your freight.';
    }

    var amount = money(best.price);
    /* Was /unlimited/i.test(best.name), which went false the moment a cart
       spanned two profiles and Shopify collapsed the name to "Shipping". */
    var unlimited = !!furniture;
    var canada = market() === 'CA';

    if (best.price === 0) {
      return dest.assumed
        ? 'Your order ships free — fully insured, however many pieces.'
        : 'Your order ships free to ' + dest.province + ' — fully insured, however many pieces.';
    }

    /* Without a destination of their own, the flat rate is stated as the
       offer rather than as "your rate", and Alaska and Hawaii are named so
       the two shoppers a year it affects are not surprised at checkout. */
    if (dest.assumed) {
      if (unlimited && canada) {
        return amount + ' Unlimited Delivery — the same flat rate to every province. Taxes shown at checkout.';
      }
      if (unlimited) {
        return amount + ' Unlimited Delivery, fully insured. Alaska and Hawaii are quoted separately.';
      }
      return amount + ' delivery — fully insured, door to door.';
    }

    var where = dest.province;
    if (unlimited && canada) {
      return amount + ' Unlimited Delivery to ' + where +
             ' — the same flat rate to every province. Taxes shown at checkout.';
    }
    if (unlimited) {
      return amount + ' Unlimited Delivery to ' + where +
             '. All inclusive of insurance, duties, and fees. No surprises.';
    }
    /* A banded lane can return several services, and "From" is honest there.
       A single rate is the price, so it must not be hedged. */
    if (count > 1) {
      return 'From ' + amount + ' to ' + where + ' — choose your service at checkout.';
    }
    return amount + ' delivery to ' + where + ' — fully insured, door to door.';
  }

  /* The cart showed the merchandise total and the button repeated it, so the
     freight only appeared at checkout — the single most common reason a cart
     is abandoned. Courtney, 2026-09-19: put the delivery in "as a line item in
     cart prior to checkout" so the total the shopper sees is the total they
     pay.

     The rows are CLONED from the theme's own Total row rather than built from
     scratch, so they inherit its markup, classes and type by construction and
     cannot drift from the theme's styling. Same technique as the warranty note
     on the product page.

     ⚠ This is a DISPLAY row, not a cart line. Adding a real delivery product
     to the cart would be charged again by Shopify at checkout, and would break
     the deposit split (the 50% plan is applied per line) and the bundle
     quantity count. Shipping is priced by the delivery profile at checkout and
     must stay there. */
  var ROW_CLASS = 'po-ship-row';

  function moneySuffix(text) {
    /* The theme renders "$2,597 USD"; the drawer sometimes renders "$2,597".
       Whatever it does, the added rows have to match it. */
    var m = (text || '').match(/\b([A-Z]{3})\s*$/);
    return m ? ' ' + m[1] : '';
  }

  function totalRows() {
    var anchors = [];
    var rows = document.querySelectorAll('.cart-recap .h-stack, cart-drawer .h-stack, .po-sum__row');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].classList && rows[i].classList.contains(ROW_CLASS)) continue;
      if (/Total:?\s*[$€£]/.test((rows[i].innerText || '').replace(/\s+/g, ' '))) anchors.push(rows[i]);
    }
    return anchors;
  }

  function setRow(row, label, value) {
    var spans = row.querySelectorAll('span');
    if (spans.length < 2) { row.textContent = label + ' ' + value; return; }
    if (spans[0].textContent !== label) spans[0].textContent = label;
    var last = spans[spans.length - 1];
    if (last.textContent !== value) last.textContent = value;
  }

  function clearRows() {
    var old = document.querySelectorAll('.' + ROW_CLASS);
    for (var i = 0; i < old.length; i++) {
      if (old[i].parentNode) old[i].parentNode.removeChild(old[i]);
    }
    /* The drawer's rows are the theme's own and are only hidden, never removed. */
    var owned = document.querySelectorAll('[data-po-ship-row], [data-po-order-row]');
    for (var j = 0; j < owned.length; j++) owned[j].hidden = true;
  }

  /* The drawer renders its own Delivery and Order total rows inside the same
     <dl> as Subtotal and Total (po-cart-savings.liquid), so they line up by
     construction. Filling those is always preferred to cloning: cloning picked
     up the cart page's left-aligned row in the drawer and the figures sat out
     of line with the ones above them. Returns false when the rows are absent —
     the cart page has no .po-sum and still needs the clone path. */
  function fillOwnedRows(best, cartCents, count, suffix, furniture) {
    var shipRows = document.querySelectorAll('[data-po-ship-row]');
    if (!shipRows.length) return false;

    for (var i = 0; i < shipRows.length; i++) {
      var row = shipRows[i];
      var label = row.querySelector('[data-po-ship-label]');
      var value = row.querySelector('[data-po-ship-value]');
      var labelText = rowLabel(furniture);
      if (label && label.textContent !== labelText) {
        label.textContent = labelText;
      }
      var amount = money(best.price) + suffix;
      if (value && value.textContent !== amount) value.textContent = amount;
      row.hidden = false;

      var orderRow = row.parentNode.querySelector('[data-po-order-row]');
      if (orderRow) {
        var ov = orderRow.querySelector('[data-po-order-value]');
        var ol = orderRow.querySelector('dt');
        var total = money(cartCents / 100 + best.price) + suffix;
        if (ov && ov.textContent !== total) ov.textContent = total;
        /* Several services means the shopper picks at checkout, so the figure
           is a floor rather than the price. */
        var want = count > 1 ? 'From' : 'Order total';
        if (ol && ol.textContent !== want) ol.textContent = want;
        orderRow.hidden = false;
      }
    }
    return true;
  }

  function paintRows(best, cartCents, count, furniture) {
    if (!best || cartCents === null || best.price === 0) { clearRows(); return; }

    var anchors = totalRows();

    /* Prefer the rows the theme owns; fall back to cloning on the cart page. */
    var suffix0 = anchors.length ? moneySuffix(anchors[0].innerText || '') : '';
    if (fillOwnedRows(best, cartCents, count, suffix0, furniture)) return;

    if (!anchors.length) { clearRows(); return; }

    for (var j = 0; j < anchors.length; j++) {
      var anchor = anchors[j];
      var suffix = moneySuffix(anchor.innerText || '');

      /* Where several services apply the shopper picks at checkout, so the
         order total is a floor rather than the price. */
      var totalLabel = count > 1 ? 'From:' : 'Order total:';
      var shipLabel = rowLabel(furniture) + ':';

      var shipRow = anchor.nextElementSibling;
      if (!shipRow || !shipRow.classList || !shipRow.classList.contains(ROW_CLASS)) {
        shipRow = anchor.cloneNode(true);
        shipRow.classList.add(ROW_CLASS);
        anchor.parentNode.insertBefore(shipRow, anchor.nextSibling);
      }
      setRow(shipRow, shipLabel, money(best.price) + suffix);

      var totalRow = shipRow.nextElementSibling;
      if (!totalRow || !totalRow.classList || !totalRow.classList.contains(ROW_CLASS)) {
        totalRow = anchor.cloneNode(true);
        totalRow.classList.add(ROW_CLASS);
        shipRow.parentNode.insertBefore(totalRow, shipRow.nextSibling);
      }
      setRow(totalRow, totalLabel, money(cartCents / 100 + best.price) + suffix);
    }
  }

  function paint(text) {
    var bars = document.querySelectorAll('free-shipping-bar');
    for (var i = 0; i < bars.length; i++) {
      var span = bars[i].querySelector('span');
      if (!span) continue;
      if (span.textContent !== text) span.textContent = text;
    }
  }

  /* ---- Cache key, and why this one bit hard -------------------------------
     The theme keeps total-price on <free-shipping-bar> and updates it as the
     cart changes, which makes it a cheap key for "has anything moved".

     ⛔ But the CART PAGE carries TWO of those elements: its own, and the
     drawer's, which is rendered on every page. querySelector returned
     whichever came first in the DOM, and the two disagree while either one is
     re-rendering — so the key flipped back and forth, every refresh missed
     cache, and each miss fired a fresh /cart/shipping_rates.json + /cart.js
     pair. The result was a permanent request loop, hundreds per page view,
     against an endpoint that is NOT read-only (see the header). The drawer on
     its own has a single bar, which is why this only showed on /cart.

     Fix: take the first bar that actually carries a value, and hold the last
     good one while they are mid-render, so a re-render can never look like a
     cart change. */
  var lastKey = '';
  function cartKey() {
    var bars = document.querySelectorAll('free-shipping-bar');
    for (var i = 0; i < bars.length; i++) {
      var value = bars[i].getAttribute('total-price');
      if (value) { lastKey = value; return value; }
    }
    return lastKey;
  }

  /* Belt and braces. Even if some future re-render defeats the key again, no
     more than one request can be in flight and none can follow inside a
     second. A stale row for one beat is worth far more than a fetch loop. */
  var inFlight = false;
  var lastFetchAt = -Infinity;
  var MIN_GAP_MS = 1000;

  function clock() {
    return (window.performance && performance.now) ? performance.now() : Date.now();
  }

  function repaintFromCache() {
    if (!cache.key) return;
    paint(cache.text);
    paintRows(cache.best, cache.cents, cache.count, cache.furniture);
  }

  function refresh() {
    var bar = document.querySelector('free-shipping-bar');
    if (!bar) return;

    var dest = destination();
    if (!dest) { paint(NO_DEST); clearRows(); return; }

    var key = cartKey() + '|' + dest.country + '|' + dest.province + '|' + dest.zip;
    if (cache.key === key) { repaintFromCache(); return; }

    /* Throttled or already asking: keep showing what we last knew rather than
       blanking the row, and let the 600ms poll pick it up. */
    if (inFlight || clock() - lastFetchAt < MIN_GAP_MS) { repaintFromCache(); return; }
    inFlight = true;
    lastFetchAt = clock();

    /* Paint the safe line while the request is in flight, so the deficit
       message is never what the shopper is left looking at. */
    paint(cache.text || NO_DEST);

    var qs = new URLSearchParams();
    qs.set('shipping_address[country]', dest.country);
    qs.set('shipping_address[province]', dest.province);
    qs.set('shipping_address[zip]', dest.zip);

    Promise.all([
      fetch('/cart/shipping_rates.json?' + qs.toString()).then(function (r) { return r.ok ? r.json() : null; }),
      fetch('/cart.js').then(function (r) { return r.ok ? r.json() : null; })
    ]).then(function (both) {
      var rates = (both[0] && both[0].shipping_rates) || [];
      var cartCents = both[1] ? both[1].total_price : null;
      var best = cheapest(rates);
      var furniture = hasFurniture(both[1]);
      var choices = priceChoices(rates);
      var text = sentence(best, choices, dest, furniture);
      cache = { key: key, text: text, best: best, cents: cartCents, count: choices, furniture: furniture };
      paint(text);
      paintRows(best, cartCents, choices, furniture);
      inFlight = false;
    }).catch(function () { inFlight = false; paint(NO_DEST); clearRows(); });
  }

  /* A frame is too eager: paintRows writes textContent, which IS a childList
     mutation, so the observer re-entered on our own writes. 150ms collapses a
     re-render burst into one pass. */
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(function () { queued = false; refresh(); }, 150);
  }

  function start() {
    refresh();
    try {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    /* The drawer is re-rendered wholesale on every cart change, so the bar we
       painted is routinely replaced by a fresh one carrying the old copy. */
    setInterval(refresh, 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();